import { useMapContext, useOverlayContext } from 'shared/context';
import { useMouseCoords, useWindowSize } from 'shared/hooks';
import { getHexPoints, Position, MapOffset } from 'shared/utils';
import { HEX } from 'shared/constants';

export const useHexGeneration = () => {
  const { map, lat, long } = useMapContext();
  const { ctx, setPaths, setHoveredHexPath } = useOverlayContext();
  const { mouseX, mouseY } = useMouseCoords();
  const [windowWidth, windowHeight] = useWindowSize();

  const isHexActive = (hexPath: Path2D) => {
    if (!map || !ctx) return;
    const containerPoint = map?.latLngToContainerPoint([lat, long]);
    return ctx.isPointInPath(hexPath, containerPoint.x, containerPoint.y);
  };

  const gridHexStyle = (hexPathStr: string) => {
    if (!ctx) return;
    const hexPath = new Path2D(hexPathStr);
    ctx.lineWidth = HEX.STROKE_WIDTH;
    ctx.strokeStyle = isHexActive(hexPath) ? '#ff000044' : '#00000011';
    ctx.fillStyle = ctx.isPointInPath(hexPath, mouseX, mouseY)
      ? '#fdc00011'
      : 'transparent';
  };

  const generateHexPaths = (
    position: Position,
    mapOffset: MapOffset,
    hexWidth: number,
  ) => {
    const points = getHexPoints(hexWidth);
    const rowOffset = position.row % 2 === 0 ? 0 : hexWidth;
    const normalOffsetX = position.col * hexWidth * 2 + rowOffset;
    const normalOffsetY = position.row * ((3 / 2) * hexWidth);
    const fullOffsetX = normalOffsetX - mapOffset.x * 2 - hexWidth;
    const fullOffsetY = normalOffsetY - mapOffset.y * 2 - (3 / 2) * hexWidth;

    const hexPoints: number[][] = points.map(point => [
      point[0] + fullOffsetX,
      point[1] + fullOffsetY,
    ]);
    const hexPathStart = hexPoints.shift() as number[];
    const hexPath: string = hexPoints.reduce(
      (path, point) => `${path} L ${point[0]} ${point[1]}`,
      `M ${hexPathStart[0]} ${hexPathStart[1]}`,
    );
    return hexPath;
  };

  const drawHex = (
    context: CanvasRenderingContext2D | null,
    position: Position,
    mapOffset: MapOffset,
    hexWidth: number,
    hexStyle?: (path: string) => void,
  ) => {
    if (!context) return null;
    const hexPath = generateHexPaths(position, mapOffset, hexWidth);
    const hexPath2D = new Path2D(hexPath);
    if (context.isPointInPath(hexPath2D, mouseX, mouseY))
      setHoveredHexPath(hexPath2D);
    if (!hexStyle) {
      gridHexStyle(hexPath);
    } else hexStyle(hexPath);

    const path = new Path2D(hexPath);

    context.fill(path);
    context.stroke(path);

    return hexPath;
  };

  const generateHexGrid = (offsetX: number, offsetY: number, scale: number) => {
    if (!ctx) return;
    const hexes = [];
    const hexWidth = HEX.WIDTH * scale;
    const cols = 2 + Math.ceil(windowWidth / (hexWidth * 2));
    const rows = 2 + Math.ceil(windowHeight / ((3 / 2) * hexWidth));
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        const hex: string | null = drawHex(
          ctx,
          { col: i, row: j },
          { x: offsetX, y: offsetY },
          hexWidth,
        );
        if (hex) {
          hexes.push(hex);
        }
      }
    }
    setPaths(hexes);
  };

  return {
    drawHex,
    generateHexPaths,
    generateHexGrid,
  };
};
